/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  useQuery as useApolloQuery,
  useLazyQuery as useApolloLazyQuery,
  gql,
  type QueryResult,
  type OperationVariables,
  ApolloClient,
  type ApolloLink,
  InMemoryCache,
  HttpLink,
  from,
  type ApolloQueryResult,
  type NormalizedCacheObject,
  type FetchResult,
  ApolloError,
  type ServerParseError,
  type ServerError,
  type DefaultContext as ApolloContext,
} from '@apollo/client'
import { Capacitor } from '@capacitor/core'
import { type GraphQLError } from 'graphql'
import fetch from 'cross-fetch'
import { setContext } from '@apollo/client/link/context'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { capacitorFetch } from './capacitor'

// Store the base HTTP link to avoid recreating it
let baseHttpLink: ApolloLink | null = null

// Suppress Apollo's cache-related invariant warnings ONLY on enrollment routes
// Enrollment mutations return incomplete/optional data structures
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  const originalError = console.error
  // eslint-disable-next-line no-console
  console.error = (...args: unknown[]) => {
    const message = String(args[0] ?? '')

    // Check if current route is an enrollment page
    const isEnrollmentRoute = window.location.pathname.includes('enrollment')

    if (isEnrollmentRoute && message.includes('go.apollo.dev/c/err')) {
      return
    }

    originalError.apply(console, args)
  }
}

/**
 * Initialize Apollo GraphQL client.
 * @param url - string
 * @returns ApolloClient
 */
export const initGraphql = (url: string, accessToken?: string): ApolloClient<NormalizedCacheObject> => {
  // Create the base HTTP link
  baseHttpLink = new HttpLink({
    uri: url,
    fetch: Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android' ? capacitorFetch : fetch,
    credentials: 'include',
  })

  // Create initial auth link (will be replaced by setAccessToken)
  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: accessToken ?? '',
      },
    }
  })

  const link = from([authLink, baseHttpLink])
  const client = new ApolloClient({
    cache: new InMemoryCache({ addTypename: false }),
    link,
  })
  graphqlSingleton.setClient(client)

  if (accessToken) {
    setAccessToken(accessToken)
  }
  return client
}

/**
 * Set the access token to the GraphQL client
 * @param accessToken - string
 * @returns void
 */
export const setAccessToken = (accessToken: string): void => {
  const client = graphqlSingleton.getClient()
  if (client && baseHttpLink) {
    // Create a new auth link with the updated token
    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: accessToken || '',
        },
      }
    })

    // Replace the entire link chain with the new auth link + base HTTP link
    client.setLink(from([authLink, baseHttpLink]))
  }
}

/**
 * Set a header to the GraphQL client
 * @param headers - headers to set
 * @returns void
 */
export const setHeaders = (headers: Record<string, string>): void => {
  const client = graphqlSingleton.getClient()
  if (client) {
    const link = client.link
    const authLink = setContext((_, { defaultHeaders }) => {
      return {
        headers: {
          ...defaultHeaders,
          ...headers,
        },
      }
    })

    client.setLink(authLink.concat(link))
  }
}

/**
 * The gqlQuery return type. Contains:
 * data: unknown
 * loading: boolean
 * error: unknown
 */
export type GQLResponse = QueryResult<unknown>

/**
 * The hook returns data, loading, error variables in order to fetch data in UI Components with GraphQL query
 * @returns GQLResponse
 */
export const useQuery = (query: string, variables: OperationVariables): GQLResponse =>
  useApolloQuery(gql(query), variables)

/**
 * The hook returns data, loading, error variables in order to fetch data in UI Components with GraphQL lazy query
 * @param query - string
 * @param variables - OperationVariables
 * @returns
 */
export const useLazyQuery = (query: string, variables: OperationVariables) => useApolloLazyQuery(gql(query), variables)

const handleSuccess = ({ data }: ApolloQueryResult<unknown>): unknown => {
  const responseData = data as { data: unknown }
  return responseData.data ? responseData.data : responseData
}

const handleMutationSuccess = ({ data }: FetchResult<unknown>): unknown => {
  const responseData = data as { data: unknown }
  return responseData.data ? responseData.data : responseData
}

/**
 * Generic errors of a GraphQL call. Includes network errors and other unknown errors.
 */
export enum ErrorCodes {
  // eslint-disable-next-line no-unused-vars
  NETWORK_ERROR = 'NETWORK_ERROR',
  // eslint-disable-next-line no-unused-vars
  UNKNOWN = 'UNKNOWN',
  // eslint-disable-next-line no-unused-vars
  UNAUTHENTICATED = 'UNAUTHENTICATED',
}

/**
 * GraphQL error type. Includes network error during a GraphQL call.
 */
export interface GQLError {
  code: ErrorCodes | string
  serviceName?: string
  stack?: GraphQLError | Error | ServerParseError | ServerError
}

const handleErrors = (error: unknown) => {
  let gqlError: GQLError = {
    code: ErrorCodes.UNKNOWN,
    stack: error as Error,
  }
  if (error instanceof ApolloError) {
    // Handle error
    const { graphQLErrors, networkError } = error
    if (graphQLErrors.length > 0) {
      const currError = graphQLErrors[0]
      if (currError.extensions?.errorType) {
        gqlError = {
          code: currError.extensions.errorType as ErrorCodes | string,
          serviceName: currError.extensions.serviceName as string,
          stack: currError as GraphQLError,
        }
      }
    } else if (networkError) {
      // Throw a GQLError
      gqlError = {
        code: ErrorCodes.NETWORK_ERROR,
        stack: networkError,
      }
    }
  }
  // Send notification
  notifications.dispatch(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, gqlError)
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- throw error
  throw gqlError
}

let instance: GraphQLClient | null = null

class GraphQLClient {
  private client?: ApolloClient<NormalizedCacheObject>

  constructor() {
    if (!instance) {
      instance = this
    }
    // eslint-disable-next-line no-constructor-return -- intentional
    return instance
  }

  setClient(client: ApolloClient<NormalizedCacheObject>) {
    this.client = client
  }

  getClient(): ApolloClient<NormalizedCacheObject> | undefined {
    return this.client
  }
}

/**
 * GraphQL Operation type
 */
export interface Operation {
  name: string
  query: string
}

/**
 * The singleton object to get graphql client
 * Must be a part of React lifecycle.
 * @returns GraphQLClient singleton object
 * @example
 * const client = graphqlSingleton.getClient();
 */
export const graphqlSingleton = new GraphQLClient()

/**
 * Async method to run a GraphQL query
 * @returns Promise<unknown>
 * @example const result = await gqlPromise('...some gql query...');
 */
export const gqlPromise = async (
  operation: Operation,
  variables?: OperationVariables,
  context?: DefaultContext,
): Promise<unknown> => {
  const client = graphqlSingleton.getClient()
  if (client) {
    return client
      .query({
        query: gql(operation.query),
        variables,
        fetchPolicy: 'no-cache',
        context,
      })
      .then(handleSuccess)
      .catch(handleErrors)
      .finally(() => {
        notifications.dispatch(NOTIFICATIONS.CLIENTS.GRAPHQL.QUERY, { query: operation.name })
      })
  }
  const gqlError: GQLError = {
    code: ErrorCodes.UNKNOWN,
    stack: new Error('Error using GraphQL - client was not initialized'),
  }
  // Send notification message
  notifications.dispatch(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, gqlError)
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- throw error
  throw gqlError
}

/**
 * Async method to run a GraphQL mutation
 * @returns Promise<unknown>
 * @example const result = await mutationPromise('...some gql mutation...');
 */
export const mutationPromise = async (
  operation: Operation,
  variables?: OperationVariables,
  context?: DefaultContext,
): Promise<unknown> => {
  const client = graphqlSingleton.getClient()
  if (client) {
    return client
      .mutate({
        mutation: gql(operation.query),
        variables,
        context,
      })
      .then(handleMutationSuccess)
      .catch(handleErrors)
      .finally(() => {
        notifications.dispatch(NOTIFICATIONS.CLIENTS.GRAPHQL.QUERY, { mutation: operation.name })
      })
  }
  const gqlError: GQLError = {
    code: ErrorCodes.UNKNOWN,
    stack: new Error('Error using GraphQL - client was not initialized'),
  }
  // Send notification message
  notifications.dispatch(NOTIFICATIONS.CLIENTS.GRAPHQL.ERROR, gqlError)
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- throw error
  throw gqlError
}

/**
 * GraphQL provider
 */
export { ApolloProvider as GraphQLProvider } from '@apollo/client'

/**
 * The default context type for the GraphQL client
 */
export type DefaultContext = ApolloContext

export type GqlClient = ApolloClient<NormalizedCacheObject>
